/*
  adg-bottom-inset — the one place that decides how high off the bottom of the
  screen our floating things sit.

  WHY THIS EXISTS
  Everything docked at the bottom used to carry a hand-measured number:
  .byo-submit at bottom:0, the live-preview dock at bottom:92px (that is 70 for
  the submit bar plus a 22 gap), the to-top pill at 88 / 261 / 26 depending on
  the page. Each number was measured once, against the others, on one page, on
  one day. Change any one thing and the rest are silently wrong.

  That is what buried the BYO Add to Cart button. byo-builder.css even carried
  a comment saying Shopify's preview banner "is not present in current
  Shopify dev-store UI" — it is present again, 68px tall, sitting exactly on
  top of the button. The same thing happens with a cookie banner, a support
  chat bubble, or anything else a third party decides to dock down there, and
  fighting them on z-index is a losing game: they use 999999 and we cannot
  out-bid an arms race we do not control.

  So do not overlap them. Measure what is actually at the bottom of the
  viewport right now and stack above it.

  WHAT IT PUBLISHES  (on :root, all in px, all live)

    --adg-inset-0   top edge of the foreign junk. Where OUR stack starts.
    --adg-inset-1   above the submit bar    (inset-0 + its height + gap)
    --adg-inset-2   above the preview dock  (inset-1 + its height + gap)

    --adg-scroll-pad-bottom   how much of the bottom of the screen is covered
                    right now: the foreign band plus our action bar when it is
                    showing, plus a little air; 0 when nothing is. adg-a11y.css
                    makes it html's scroll-padding-bottom, so a newly focused
                    element is scrolled clear of the bar instead of behind it
                    (WCAG 2.4.11 Focus not obscured).

  Each layer is "the floor for whatever sits above me", so a consumer never
  needs to know what is below it — only which layer it belongs to. Adding a
  fourth floating thing later means adding --adg-inset-3, not re-measuring
  three other files.

  WHAT COUNTS AS FOREIGN
  Fixed or sticky, visibly painted, at least 16px tall, and docked within 8px
  of the viewport bottom: its bottom edge AT the bottom, not running past it,
  and a sticky one only if it sticks by its bottom (bottom: not auto). Tall
  in-flow content like the cart summary is neither. It must also span most of the width: a full-width
  bar genuinely steals that band of screen, but a little corner chat bubble
  does not, and lifting the entire UI for one would be worse than the overlap.
  Anything of ours is skipped by [data-adg-bottom] — see OWNED below.

  Recomputes on resize, scroll (sticky bars move), and DOM mutation, so a
  cookie banner that appears late is picked up and dismissing it drops the
  inset back to zero. All of it is rAF-debounced to one pass per frame.
*/
(function () {
  'use strict';

  // Ours. Never treat these as obstructions — they ARE the stack.
  var OWNED = '[data-adg-bottom], .byo-submit, .adg-totop, .byo-tray';

  var GAP = 22;          // breathing room between stacked layers
  var MIN_HEIGHT = 16;   // shorter than this is a hairline, not a bar
  var MIN_WIDTH_RATIO = 0.55;
  var DOCK_TOLERANCE = 8; // "docked" means within this of the viewport bottom

  var root = document.documentElement;
  var queued = false;
  var last = { 0: -1, 1: -1, 2: -1 };
  var lastPad = -1;
  var FOCUS_AIR = 8;      // keep a focus ring off the bar's top edge

  // The preview dock lives in a shadow root, so it cannot be measured from
  // here. It reports its own height instead — see adg-preview-shell.js.
  var dockHeight = 0;

  function foreignInset() {
    var vh = window.innerHeight;
    var vw = window.innerWidth;
    var inset = 0;
    var nodes = document.body ? document.body.querySelectorAll('*') : [];

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.closest && el.closest(OWNED)) continue;

      var cs;
      try { cs = getComputedStyle(el); } catch (e) { continue; }
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      // A sticky element only docks at the bottom if it sticks BY its bottom.
      // One that sticks by its top (the cart page's order summary) is page
      // content scrolling past; counting it lifted the bar 192px up the cart
      // on phones, over the order (customer complaint via Nathan, 10-05).
      if (cs.position === 'sticky' && cs.bottom === 'auto') continue;
      if (cs.visibility === 'hidden' || cs.display === 'none') continue;
      if (parseFloat(cs.opacity) < 0.05) continue;

      var r = el.getBoundingClientRect();
      if (r.height < MIN_HEIGHT) continue;
      if (r.width < vw * MIN_WIDTH_RATIO) continue;      // corner widget, ignore
      if (r.bottom < vh - DOCK_TOLERANCE) continue;      // floating, not docked
      if (r.bottom > vh + DOCK_TOLERANCE) continue;      // runs past the bottom: content, not a dock
      if (r.top > vh) continue;                          // off-screen below
      // Full-screen overlays (the header menu scrim, the drawer, dialog
      // backdrops) reach the bottom too but are not bars: padding the page by a
      // whole screen scrolled every focused menu link out of view (RC check B,
      // 2026-10-07).
      if (r.top <= 0) continue;

      var occupies = vh - r.top;
      if (occupies > inset) inset = occupies;
    }
    return Math.max(0, Math.round(inset));
  }

  function ownHeight(sel) {
    var el = document.querySelector(sel);
    if (!el) return 0;
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return 0;
    var h = el.getBoundingClientRect().height;
    return h > 1 ? Math.round(h) : 0;
  }

  function measure() {
    queued = false;

    var i0 = foreignInset();

    var submit = ownHeight('.byo-submit');
    var i1 = i0 + (submit ? submit + GAP : 0);
    var i2 = i1 + (dockHeight ? dockHeight + GAP : 0);

    set(0, i0); set(1, i1); set(2, i2);

    var cover = Math.max(i0, barCover(i0));
    var pad = cover ? cover + FOCUS_AIR : 0;
    var grew = pad > lastPad;
    if (pad !== lastPad) { lastPad = pad; root.style.setProperty('--adg-scroll-pad-bottom', pad + 'px'); }
    if (grew) keepFocusClear(pad);

    // The live-preview dock measures 118x307 — much taller than the 157 an old
    // comment claimed, which is why the pill's hardcoded 261px was landing
    // inside it. Stacking above a 307px dock would strand the pill a third of
    // the way up the screen, so when the dock is out we move the pill to the
    // other corner instead, where nothing is docked at all.
    root.toggleAttribute('data-adg-dock', dockHeight > 0);
  }

  // The action bar (global, grid and BYO submit bar are all .adg-actionbar)
  // sits on --adg-inset-0, so what it covers is inset-0 plus its own height.
  // A reveal-on-scroll bar counts only while it is shown: its class is the
  // truth, since its fade-out keeps visibility:visible for a moment.
  // offsetHeight ignores the slide-in transform, so a measurement taken
  // mid-animation is already the final one.
  function barCover(i0) {
    var bars = document.querySelectorAll('.adg-actionbar');
    var h = 0;
    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      if (el.classList.contains('adg-actionbar--utility-only') && !el.classList.contains('is-visible')) continue;
      var cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      if (el.offsetHeight > h) h = el.offsetHeight;
    }
    return h ? i0 + h : 0;
  }

  // The scroll-padding only helps the NEXT focus move. The reveal-on-scroll
  // utility bar appears because focusing scrolled the page past 1.5 screens,
  // so it can slide in over the element that was just scrolled into view
  // (the footer's email field on the studio About page and the Studio
  // Journal, 2026-10-17 audit). When the covered band grows, move the
  // focused element clear of it (WCAG 2.4.11 Focus not obscured).
  function keepFocusClear(pad) {
    var el = document.activeElement;
    if (!pad || !el || el === document.body || el === root) return;
    if (el.closest && el.closest(OWNED + ', .adg-actionbar')) return;
    var r = el.getBoundingClientRect();
    if (!r.height || r.top >= window.innerHeight || r.bottom <= 0) return;
    var limit = window.innerHeight - pad;
    if (r.bottom > limit) window.scrollBy(0, Math.min(r.bottom - limit, Math.max(0, r.top)));
  }

  function set(layer, px) {
    if (last[layer] === px) return;       // don't churn style on every scroll
    last[layer] = px;
    root.style.setProperty('--adg-inset-' + layer, px + 'px');
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(measure);
  }

  // Let the shadow-DOM dock tell us how tall it is.
  window.adgSetDockHeight = function (px) {
    var n = Math.max(0, Math.round(px || 0));
    if (n === dockHeight) return;
    dockHeight = n;
    schedule();
  };

  schedule();
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('load', schedule);
  document.addEventListener('DOMContentLoaded', schedule);

  // A cookie banner or chat widget injected after load, or dismissed later.
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
  }
  // The submit bar changes height when its label wraps on a phone.
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(schedule);
    var watch = document.querySelector('.byo-submit');
    if (watch) ro.observe(watch);
    document.addEventListener('DOMContentLoaded', function () {
      var late = document.querySelector('.byo-submit');
      if (late && late !== watch) ro.observe(late);
    });
  }
})();
